import { useState, useEffect } from "react";
import ChevronDownIcon from "../../assets/svg/chevron-down.svg";
import { getPaymentPreview, savePayment } from "../../api/discussion";
import { toast } from "sonner";

function CurrencyAmountField({
  label,
  value,
  onChange,
  readOnly = false,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  readOnly?: boolean;
}) {
  return (
    <div
      className="flex flex-col items-start gap-1"
      style={{ flex: "1 1 0", minWidth: 0 }}
    >
      <label
        className="flex items-center text-[12px] font-normal leading-5 text-[#222222]"
        style={{ fontFamily: "Inter, sans-serif", fontFeatureSettings: "'ss01' on, 'cv01' on" }}
      >
        {label}
      </label>
      <div
        className="flex flex-row justify-between items-center w-full h-9 px-0 gap-2.5"
        style={{
          background: "#FFFFFF",
          border: "1px solid #EEEEEE",
          borderRadius: "6px",
          boxSizing: "border-box",
        }}
      >
        <input
          type="text"
          inputMode="decimal"
          value={value}
          onChange={(e) => {
            if (readOnly) return;
            const cleaned = e.target.value.replace(/[^\d.]/g, "");
            const parts = cleaned.split(".");
            const next =
              parts.length > 1
                ? `${parts[0]}.${parts.slice(1).join("")}`
                : cleaned;
            onChange(next);
          }}
          onKeyDown={(e) => {
            if (readOnly) return;
            if (
              e.key.length === 1 &&
              !e.ctrlKey &&
              !e.metaKey &&
              !/[\d.]/.test(e.key)
            ) {
              e.preventDefault();
            }
          }}
          readOnly={readOnly}
          placeholder="00.00"
          className="flex-1 min-w-0 h-full pl-2.5 pr-0 py-0 bg-transparent text-[12px] font-normal leading-5 text-[#222222] placeholder:text-[#6D6D6D] border-0 outline-none focus:ring-0 disabled:bg-transparent disabled:cursor-default"
          style={{ fontFamily: "Inter, sans-serif", fontFeatureSettings: "'ss01' on, 'cv01' on" }}
        />
        <div
          className="flex flex-row justify-center items-center shrink-0 px-2.5 py-2 gap-2.5 h-9 min-w-[76px]"
          style={{
            background: "#F7F7F7",
            borderRadius: "0 6px 6px 0",
          }}
        >
          <span
            className="text-[12px] font-normal leading-5 text-[#6D6D6D]"
            style={{ fontFamily: "Inter, sans-serif", fontFeatureSettings: "'ss01' on, 'cv01' on" }}
          >
            Rs(₹)
          </span>
          <img
            src={ChevronDownIcon}
            alt=""
            className="w-4 h-4 shrink-0"
            aria-hidden
          />
        </div>
      </div>
    </div>
  );
}

function formatAmount(num: number | null | undefined): string {
  if (num == null || !Number.isFinite(num) || num === 0) return "";
  return num.toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

function parseAmount(s: string): number {
  const cleaned = String(s || "").replace(/[^\d.]/g, "");
  const parsed = parseFloat(cleaned);
  return isNaN(parsed) ? 0 : parsed;
}

type PaymentFromClientViewProps = {
  leadId?: string | null;
  onSuccess?: (leadId: string) => void;
  onSavingChange?: (saving: boolean) => void;
};

export default function PaymentFromClientView({
  leadId,
  onSuccess,
  onSavingChange,
}: PaymentFromClientViewProps) {
  const [loading, setLoading] = useState(!!leadId);
  const [invoiceAmount, setInvoiceAmount] = useState<number>(0);
  const [savedCollected, setSavedCollected] = useState<number>(0);
  const [collectedAmount, setCollectedAmount] = useState<string>("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!leadId) {
      setLoading(false);
      return;
    }
    let cancelled = false;
    setLoading(true);
    getPaymentPreview(leadId)
      .then((preview) => {
        if (cancelled) return;
        setInvoiceAmount(Number(preview.invoiceAmount ?? 0));
        setSavedCollected(Number(preview.collectedAmount ?? 0));
        setCollectedAmount("");
      })
      .catch((err) => {
        if (!cancelled) {
          toast.error(err instanceof Error ? err.message : "Failed to load payment details");
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [leadId]);

  const thisPayment = parseAmount(collectedAmount);
  const totalCollected = savedCollected + thisPayment;
  const outstanding = Math.max(0, invoiceAmount - totalCollected);
  const outstandingFormatted = outstanding.toLocaleString("en-IN", {
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  });

  const handleSaveAndContinue = async () => {
    if (!leadId || saving) return;
    if (thisPayment <= 0) {
      toast.error("Collected amount should be greater than 0. Use Progress to skip this step.");
      return;
    }
    if (savedCollected >= invoiceAmount) {
      toast.error("This invoice is already fully paid.");
      return;
    }
    if (totalCollected > invoiceAmount) {
      toast.error("Collected amount cannot exceed the outstanding invoice balance.");
      return;
    }
    setSaving(true);
    onSavingChange?.(true);
    try {
      await savePayment(leadId, { collectedAmount: thisPayment });
      setSavedCollected(totalCollected);
      setCollectedAmount("");
      toast.success("Payment from client recorded successfully.");
      onSuccess?.(leadId);
    } catch (err) {
      const msg = err instanceof Error ? err.message : "Failed to save payment";
      if (msg.toLowerCase().includes("generate") || msg.toLowerCase().includes("pdf first")) {
        toast.error("Please generate the invoice PDF first (Invoice to Client step).");
      } else {
        toast.error(msg);
      }
    } finally {
      setSaving(false);
      onSavingChange?.(false);
    }
  };

  if (!leadId) {
    return (
      <div
        className="flex flex-col items-start w-full max-w-[1085px] gap-5"
        style={{ fontFamily: "Inter, sans-serif" }}
      >
        <p className="text-xs text-[#6D6D6D]">
          Please complete the previous steps to access this form.
        </p>
      </div>
    );
  }

  if (loading) {
    return (
      <div
        className="flex flex-col items-start w-full max-w-[1085px] gap-5 justify-center py-12"
        style={{ fontFamily: "Inter, sans-serif" }}
      >
        <p className="text-sm text-[#6D6D6D]">Loading payment details...</p>
      </div>
    );
  }

  return (
    <form
      id="payment-form"
      onSubmit={(e) => {
        e.preventDefault();
        handleSaveAndContinue();
      }}
      className="flex flex-col items-start w-full max-w-[1085px] gap-5"
      style={{ fontFamily: "Inter, sans-serif" }}
    >
      {saving && <p className="text-xs text-[#6D6D6D]">Saving...</p>}

      {/* Row 1: Invoice Amount + Collected Amount */}
      <div
        className="flex flex-col items-start gap-[22px] w-full"
        style={{ width: "1085px", maxWidth: "100%" }}
      >
        <div
          className="flex flex-col sm:flex-row items-start sm:items-start gap-5 w-full"
        >
          <CurrencyAmountField
            label="Invoice Amount (Fetched)"
            value={formatAmount(invoiceAmount)}
            onChange={() => {}}
            readOnly
          />
          <div className="flex flex-col gap-1" style={{ flex: "1 1 0", minWidth: 0 }}>
            <CurrencyAmountField
              label="Collected Amount for New filing"
              value={collectedAmount}
              onChange={setCollectedAmount}
            />
            {savedCollected > 0 && (
              <span className="text-[11px] leading-4 text-[#6D6D6D]">
                Already collected: Rs.{" "}
                {savedCollected.toLocaleString("en-IN", {
                  minimumFractionDigits: 0,
                  maximumFractionDigits: 2,
                })}
                /-
              </span>
            )}
          </div>
        </div>

        {/* Outstanding Amount bar */}
        <div
          className="flex flex-row justify-between items-center w-full h-[60px] px-5 gap-3"
          style={{
            background: "#222222",
            borderRadius: "8px",
          }}
        >
          <span
            className="text-[14px] font-normal leading-5"
            style={{
              fontFamily: "Inter, sans-serif",
              color: "rgba(255, 255, 255, 0.9)",
            }}
          >
            Outstanding Amount for New filing
          </span>
          <span
            className="text-[22px] font-bold leading-10 text-white"
            style={{ fontFamily: "Inter, sans-serif" }}
          >
            Rs. {outstandingFormatted}/-
          </span>
        </div>
      </div>

    </form>
  );
}
